<script setup lang="ts">
/**
 * Compact ticket edit header — number, title, action buttons.
 * Status/priority are shown in the Properties sidebar, not here.
 */
import SharedClipboardText from '../shared/SharedClipboardText.vue';
import TicketEmailButton from 'src/components/ticket/TicketEmailButton.vue';
import TicketPrintButton from 'src/components/ticket/TicketPrintButton.vue';

defineProps<{
  ticketNumber?: number;
  title?: string;
  ticketId: string;
}>();

const emit = defineEmits<{ close: [] }>();
</script>

<template>
  <header class="flex items-center justify-between pr-2 py-2 shrink-0">
    <div class="flex items-center gap-3 min-w-0">
      <SharedClipboardText
        v-if="ticketNumber"
        prefix="#"
        :text="String(ticketNumber)"
        class="text-xl font-bold text-surface-900 dark:text-white tracking-tight shrink-0"
      />
      <span v-if="title" class="text-sm text-surface-500 dark:text-surface-400 truncate">
        {{ title }}
      </span>
    </div>

    <div class="flex items-center gap-0.5 shrink-0">
      <div class="header-action"><TicketEmailButton :ticket-id="ticketId" /></div>
      <div class="header-action"><TicketPrintButton :ticket-id="ticketId" /></div>
      <div class="w-px h-5 bg-surface-200 dark:bg-surface-700 mx-1" />
      <button
        class="header-btn hover:!text-red-500 hover:!bg-red-50 dark:hover:!bg-red-900/20"
        @click="emit('close')"
      >
        <i class="pi pi-times" style="font-size: 14px" />
      </button>
    </div>
  </header>
</template>

<style scoped>
.header-btn {
  padding: 0.5rem;
  border-radius: 0.5rem;
  color: var(--p-surface-400);
  transition: all 0.15s;
}
.header-btn:hover {
  color: var(--p-surface-700);
  background: var(--p-surface-100);
}
:root[class*='app-dark'] .header-btn:hover {
  color: var(--p-surface-200);
  background: var(--p-surface-800);
}

/* Strip PrimeVue Button styling from email/print to match icon-only header buttons */
.header-action :deep(.p-button) {
  padding: 0.5rem;
  border-radius: 0.5rem;
  color: var(--p-surface-400);
  background: transparent;
  border: none;
  box-shadow: none;
  transition: all 0.15s;
  gap: 0;
}
.header-action :deep(.p-button:hover) {
  color: var(--p-surface-700);
  background: var(--p-surface-100);
}
:root[class*='app-dark'] .header-action :deep(.p-button:hover) {
  color: var(--p-surface-200);
  background: var(--p-surface-800);
}
/* Hide button label text, show only icon */
.header-action :deep(.p-button-label) {
  display: none;
}
.header-action :deep(.p-button-icon) {
  font-size: 14px;
  margin: 0;
}
</style>
